(function () {
  if (customElements.get('custom-announcement-bar')) return;
  class CustomAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.observer = null;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (!('IntersectionObserver' in window)) {
        this.initializeSlider();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.initializeSlider();
        },
        { rootMargin: '0px 0px 200px 0px' }
      );
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.destroySlider();
      delete this.dataset.initialized;
    }
    destroySlider() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }
    initializeSlider() {
      const element = this.querySelector('.custom-announcement__swiper');
      if (!element) return;
      const count = element.querySelectorAll('.swiper-slide').length;
      this.destroySlider();
      if (count < 2) return;
      if (typeof Swiper === 'undefined') {
        console.warn('Custom Announcement Bar requires Swiper to be loaded.');
        return;
      }
      const autoplayEnabled = this.dataset.autoplay === 'true';
      const delay = Number(this.dataset.delay) || 5000;
      const autoplay = autoplayEnabled && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slider = new Swiper(element, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        speed: 600,
        loop: autoplay,
        watchOverflow: true,
        allowTouchMove: true,
        autoHeight: false,
        autoplay: autoplay ? { delay, disableOnInteraction: false } : false,
      });
    }
    onBlockSelect(event) {
      const slide = event.target.closest('.swiper-slide');
      if (!slide || !this.contains(slide)) return;
      if (!this.slider) this.initializeSlider();
      if (!this.slider) return;
      this.slider.autoplay?.stop();
      const index = Number(slide.getAttribute('data-swiper-slide-index'));
      if (this.slider.params.loop && Number.isInteger(index)) this.slider.slideToLoop(index, 0);
      else this.slider.slideTo(this.slider.slides.indexOf(slide), 0);
    }
    onBlockDeselect() {
      if (
        this.slider?.params.autoplay &&
        this.slider.autoplay &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        this.slider.autoplay.start();
    }
  }
  customElements.define('custom-announcement-bar', CustomAnnouncementBar);
})();

(function(){
  if(customElements.get('custom-banner-slideshow')) return;
  class CustomBannerSlideshow extends HTMLElement {
    constructor(){
      super();
      this.swiper=null;
      this.intersectionObserver=null;
      this.resizeObserver=null;
      this.mediaQuery=null;
      this.activeDevice=null;
      this.lastWidth=0;
      this.resizeFrame=0;
      this.refreshFrame=0;
      this.loadAttempts=0;
      this.loadTimer=null;
      this.onResize=this.onResize.bind(this);
      this.onBlockSelect=this.onBlockSelect.bind(this);
      this.onBlockDeselect=this.onBlockDeselect.bind(this);
    }
    connectedCallback(){
      if(this.dataset.cbsInitialized==='true') return;
      this.dataset.cbsInitialized='true';
      this.mediaQuery=window.matchMedia('(max-width: 749px)');
      if(this.mediaQuery.addEventListener) this.mediaQuery.addEventListener('change',this.onResize);
      else this.mediaQuery.addListener(this.onResize);
      window.addEventListener('resize',this.onResize,{passive:true});
      document.addEventListener('shopify:block:select',this.onBlockSelect);
      document.addEventListener('shopify:block:deselect',this.onBlockDeselect);
      const stage=this.querySelector('.custom-banner-slider__stage');
      if(stage && 'ResizeObserver' in window){
        this.resizeObserver=new ResizeObserver(entries=>{
          const w=entries[0].contentRect.width;
          if(Math.abs(w-this.lastWidth)<0.5) return;
          this.lastWidth=w;
          this.onResize();
        });
        this.resizeObserver.observe(stage);
      }
      if('IntersectionObserver' in window){
        this.intersectionObserver=new IntersectionObserver(entries=>{
          if(!entries[0].isIntersecting) return;
          this.intersectionObserver.disconnect();
          this.intersectionObserver=null;
          this.initialize();
        },{rootMargin:'0px 0px 300px 0px'});
        this.intersectionObserver.observe(this);
      }else this.initialize();
      this.querySelectorAll('.custom-banner-slider__image').forEach(img=>img.addEventListener('load',this.onResize));
    }
    getDevice(){return this.mediaQuery && this.mediaQuery.matches?'mobile':'desktop';}
    getInitialIndex(){return this.swiper && !this.swiper.destroyed?this.swiper.realIndex||0:0;}
    initialize(index=0){
      if(this.swiper || !this.isConnected) return;
      if(typeof Swiper==='undefined'){
        if(this.loadAttempts++<60) this.loadTimer=setTimeout(()=>{this.loadTimer=null;this.initialize(index);},100);
        else console.warn('Custom banner swiper: Swiper library was not loaded.');
        return;
      }
      this.loadAttempts=0;
      const slider=this.querySelector('.custom-banner-slider__swiper');
      if(!slider) return;
      const count=slider.querySelectorAll('.custom-banner-slider__slide').length;
      if(!count) return;
      const device=this.getDevice();
      const suffix=device==='mobile'?'Mobile':'Desktop';
      const autoplayEnabled=this.dataset['autoplay'+suffix]==='true';
      const originalHeight=this.dataset['height'+suffix]==='original';
      const delay=Number(this.dataset['delay'+suffix])||5000;
      const prev=this.querySelector('[data-cbs-prev]');
      const next=this.querySelector('[data-cbs-next]');
      const dots=this.querySelector('[data-cbs-pagination]');
      this.activeDevice=device;
      this.swiper=new Swiper(slider,{
        slidesPerView:1,
        spaceBetween:0,
        speed:600,
        initialSlide:Math.max(0,Math.min(index,count-1)),
        autoHeight:originalHeight,
        loop:count>1 && autoplayEnabled,
        watchOverflow:true,
        updateOnWindowResize:true,
        observer:false,
        autoplay:count>1 && autoplayEnabled?{delay:delay,disableOnInteraction:false,pauseOnMouseEnter:true}:false,
        navigation:count>1 && prev && next?{nextEl:next,prevEl:prev}:false,
        pagination:count>1 && dots?{el:dots,clickable:true}:false,
        on:{
          slideChangeTransitionEnd:swiper=>{if(swiper.params.autoHeight) swiper.updateAutoHeight(0);},
          imagesReady:swiper=>{if(swiper.params.autoHeight) swiper.updateAutoHeight(0);}
        }
      });
      this.refresh();
    }
    refresh(){
      if(!this.swiper || this.swiper.destroyed) return;
      this.swiper.update();
      if(this.swiper.params.autoHeight) this.swiper.updateAutoHeight(0);
    }
    recreate(index){
      if(this.swiper && !this.swiper.destroyed) this.swiper.destroy(true,true);
      this.swiper=null;
      const slider=this.querySelector('.custom-banner-slider__swiper');
      const wrapper=slider && slider.querySelector('.swiper-wrapper');
      if(slider) slider.style.removeProperty('height');
      if(wrapper) wrapper.style.removeProperty('height');
      this.initialize(index);
    }
    onResize(){
      if(this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame=requestAnimationFrame(()=>{
        this.resizeFrame=0;
        if(!this.isConnected) return;
        if(!this.swiper){
          if(!this.intersectionObserver) this.initialize();
          return;
        }
        if(this.getDevice()!==this.activeDevice) this.recreate(this.getInitialIndex());
        else this.refresh();
        if(this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame=requestAnimationFrame(()=>{this.refreshFrame=0;this.refresh();});
      });
    }
    onBlockSelect(e){
      if(!this.contains(e.target)) return;
      const slide=e.target.closest('[data-banner-slide-index]');
      if(!slide) return;
      if(this.intersectionObserver){this.intersectionObserver.disconnect();this.intersectionObserver=null;}
      this.initialize();
      if(!this.swiper) return;
      const index=Number(slide.dataset.bannerSlideIndex);
      if(!Number.isInteger(index)) return;
      if(this.swiper.params.loop && this.swiper.slideToLoop) this.swiper.slideToLoop(index,0);
      else this.swiper.slideTo(index,0);
      if(this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      this.refresh();
    }
    onBlockDeselect(e){
      if(!this.contains(e.target) || !this.swiper) return;
      if(this.swiper.params.autoplay && this.swiper.autoplay && !this.swiper.autoplay.running) this.swiper.autoplay.start();
    }
    disconnectedCallback(){
      document.removeEventListener('shopify:block:select',this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect',this.onBlockDeselect);
      window.removeEventListener('resize',this.onResize);
      if(this.loadTimer!==null) clearTimeout(this.loadTimer);
      this.loadTimer=null;
      if(this.mediaQuery){
        if(this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener('change',this.onResize);
        else this.mediaQuery.removeListener(this.onResize);
      }
      if(this.intersectionObserver) this.intersectionObserver.disconnect();
      if(this.resizeObserver) this.resizeObserver.disconnect();
      if(this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if(this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.querySelectorAll('.custom-banner-slider__image').forEach(img=>img.removeEventListener('load',this.onResize));
      if(this.swiper && !this.swiper.destroyed) this.swiper.destroy(true,true);
      this.swiper=null;
      this.mediaQuery=null;
      this.intersectionObserver=null;
      this.resizeObserver=null;
      this.activeDevice=null;
      delete this.dataset.cbsInitialized;
    }
  }
  customElements.define('custom-banner-slideshow',CustomBannerSlideshow);
})();

class CustomLogoIcon extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.media = null;
    this.resizeObserver = null;
    this.currentDevice = null;
    this.lastWidth = 0;
    this.resizeFrame = null;
    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.media = window.matchMedia('(max-width: 749px)');
    if (this.media.addEventListener) this.media.addEventListener('change', this.handleBreakpoint);
    else this.media.addListener(this.handleBreakpoint);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('resize', this.handleResize, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width || 0;
        if (Math.abs(this.lastWidth - width) < 1) return;
        this.lastWidth = width;
        this.handleResize();
      });
      this.resizeObserver.observe(this);
    }
    this.syncLayout();
  }
  getDevice() {
    return this.media?.matches ? 'mobile' : 'desktop';
  }
  removeClones() {
    this.querySelectorAll('.custom-logo-icon__card[data-loop-clone]').forEach((card) => card.remove());
  }
  syncLayout(index = 0) {
    if (!this.isConnected) return;
    const device = this.getDevice();
    const layout = this.dataset[device + 'Layout'];
    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.removeClones();
    this.currentDevice = device;
    this.dataset.activeLayout = layout;
    const slider = this.querySelector('.custom-logo-icon__slider');
    if (!slider) return;
    if (layout !== 'slider') {
      slider.removeAttribute('aria-roledescription');
      return;
    }
    slider.setAttribute('aria-roledescription', 'carousel');
    if (typeof Swiper === 'undefined') {
      console.warn('Custom Logo Icon: Swiper must load before theme.js.');
      return;
    }
    const wrapper = this.querySelector('.custom-logo-icon__wrapper');
    const originalCards = Array.from(wrapper.querySelectorAll('.custom-logo-icon__card'));
    const slides = originalCards.length;
    const perView = Math.max(1, Number(this.dataset[device === 'mobile' ? 'slidesMobile' : 'slidesDesktop']) || 1);
    const gap = Math.max(0, Number(this.dataset[device === 'mobile' ? 'gapMobile' : 'gapDesktop']) || 0);
    const autoplayEnabled = this.dataset[device === 'mobile' ? 'autoplayMobile' : 'autoplayDesktop'] === 'true' && slides > 1;
    const delay = Math.max(2000, Number(this.dataset[device === 'mobile' ? 'delayMobile' : 'delayDesktop']) || 5000);
    const canLoop = autoplayEnabled;

    if (canLoop) {
      const minimumSlides = Math.max(Math.ceil(perView) * 2 + 2, 4);
      let totalSlides = slides;
      let cloneIndex = 0;
      while (totalSlides < minimumSlides) {
        const clone = originalCards[cloneIndex % slides].cloneNode(true);
        clone.dataset.loopClone = 'true';
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        wrapper.appendChild(clone);
        totalSlides++;
        cloneIndex++;
      }
    }

    const prev = this.querySelector('[data-card-prev]');
    const next = this.querySelector('[data-card-next]');
    const dots = this.querySelector('[data-card-dots]');
    this.swiper = new Swiper(slider, {
      slidesPerView: perView,
      spaceBetween: gap,
      speed: 500,
      initialSlide: Math.min(index, Math.max(0, slides - 1)),
      watchOverflow: true,
      loop: canLoop,
      rewind: false,
      autoplay: autoplayEnabled ? {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      navigation: prev && next ? { nextEl: next, prevEl: prev } : false,
      pagination: dots ? { el: dots, clickable: true } : false
    });
  }
  handleResize() {
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      if (!this.isConnected) return;
      if (this.currentDevice !== this.getDevice()) {
        const index = this.swiper?.realIndex || 0;
        this.syncLayout(index);
      } else if (this.swiper && !this.swiper.destroyed) {
        this.swiper.update();
      }
    });
  }
  handleBreakpoint() {
    this.handleResize();
  }
  handleBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const card = event.target.closest('[data-card-index]');
    if (!card) return;
    const index = Number(card.dataset.cardIndex);
    if (!this.swiper) return;
    if (this.swiper.params.loop) this.swiper.slideToLoop(index, 0);
    else this.swiper.slideTo(index, 0);
    if (this.swiper.autoplay?.running) this.swiper.autoplay.stop();
  }
  disconnectedCallback() {
    if (this.media) {
      if (this.media.removeEventListener) this.media.removeEventListener('change', this.handleBreakpoint);
      else this.media.removeListener(this.handleBreakpoint);
    }
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
    this.removeClones();
    this.swiper = null;
    this.media = null;
    this.resizeObserver = null;
    this.currentDevice = null;
    delete this.dataset.initialized;
  }
}
if (!customElements.get('custom-logo-icon')) customElements.define('custom-logo-icon', CustomLogoIcon);


class CustomMultiCard extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.media = null;
    this.resizeObserver = null;
    this.currentDevice = null;
    this.lastWidth = 0;
    this.resizeFrame = null;
    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.media = window.matchMedia('(max-width: 749px)');
    if (this.media.addEventListener) this.media.addEventListener('change', this.handleBreakpoint);
    else this.media.addListener(this.handleBreakpoint);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('resize', this.handleResize, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width || 0;
        if (Math.abs(this.lastWidth - width) < 1) return;
        this.lastWidth = width;
        this.handleResize();
      });
      this.resizeObserver.observe(this);
    }
    this.syncLayout();
  }
  getDevice() {
    return this.media?.matches ? 'mobile' : 'desktop';
  }
  removeClones() {
    this.querySelectorAll('.custom-multi-card__card[data-loop-clone]').forEach((card) => card.remove());
  }
  syncLayout(index = 0) {
    if (!this.isConnected) return;
    const device = this.getDevice();
    const layout = this.dataset[device + 'Layout'];
    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.removeClones();
    this.currentDevice = device;
    this.dataset.activeLayout = layout;
    const slider = this.querySelector('.custom-multi-card__slider');
    if (!slider) return;
    if (layout !== 'slider') {
      slider.removeAttribute('aria-roledescription');
      return;
    }
    slider.setAttribute('aria-roledescription', 'carousel');
    if (typeof Swiper === 'undefined') {
      console.warn('Custom Multi Card: Swiper must load before theme.js.');
      return;
    }
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;
    const originalCards = Array.from(wrapper.querySelectorAll('.custom-multi-card__card'));
    const slides = originalCards.length;
    const perView = Math.max(1, Number(this.dataset[device === 'mobile' ? 'slidesMobile' : 'slidesDesktop']) || 1);
    const gap = Math.max(0, Number(this.dataset[device === 'mobile' ? 'gapMobile' : 'gapDesktop']) || 0);
    const autoplayEnabled = this.dataset[device === 'mobile' ? 'autoplayMobile' : 'autoplayDesktop'] === 'true' && slides > 1;
    const delay = Math.max(2000, Number(this.dataset[device === 'mobile' ? 'delayMobile' : 'delayDesktop']) || 5000);
    const canLoop = autoplayEnabled;

    if (canLoop) {
      const minimumSlides = Math.max(Math.ceil(perView) * 2 + 2, 4);
      let totalSlides = slides;
      let cloneIndex = 0;
      while (totalSlides < minimumSlides) {
        const clone = originalCards[cloneIndex % slides].cloneNode(true);
        clone.dataset.loopClone = 'true';
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        wrapper.appendChild(clone);
        totalSlides++;
        cloneIndex++;
      }
    }

    const prev = this.querySelector('[data-card-prev]');
    const next = this.querySelector('[data-card-next]');
    const dots = this.querySelector('[data-card-dots]');
    this.swiper = new Swiper(slider, {
      slidesPerView: perView,
      spaceBetween: gap,
      speed: 500,
      initialSlide: Math.min(index, Math.max(0, slides - 1)),
      watchOverflow: true,
      loop: canLoop,
      rewind: false,
      autoplay: autoplayEnabled ? {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      navigation: prev && next ? { nextEl: next, prevEl: prev } : false,
      pagination: dots ? { el: dots, clickable: true } : false
    });
  }
  handleResize() {
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      if (!this.isConnected) return;
      if (this.currentDevice !== this.getDevice()) {
        const index = this.swiper?.realIndex || 0;
        this.syncLayout(index);
      } else if (this.swiper && !this.swiper.destroyed) {
        this.swiper.update();
      }
    });
  }
  handleBreakpoint() {
    this.handleResize();
  }
  handleBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const card = event.target.closest('[data-card-index]');
    if (!card || !this.swiper) return;
    const index = Number(card.dataset.cardIndex);
    if (this.swiper.params.loop) this.swiper.slideToLoop(index, 0);
    else this.swiper.slideTo(index, 0);
    if (this.swiper.autoplay?.running) this.swiper.autoplay.stop();
  }
  disconnectedCallback() {
    if (this.media) {
      if (this.media.removeEventListener) this.media.removeEventListener('change', this.handleBreakpoint);
      else this.media.removeListener(this.handleBreakpoint);
    }
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
    this.removeClones();
    this.swiper = null;
    this.media = null;
    this.resizeObserver = null;
    this.currentDevice = null;
    delete this.dataset.initialized;
  }
}
if (!customElements.get('custom-multi-card')) customElements.define('custom-multi-card', CustomMultiCard);

